import React, { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, PieChart, Pie, Cell } from 'recharts';
import { extractLogsService } from '../../services/api';
import { getErrorMessage } from '../../utils/errors';

const FILE_TYPE_COLORS = {
    ATB: '#6366f1',
    ZRO: '#10b981',
    BIL: '#f59e0b',
    NOT: '#ef4444',
    PMT: '#8b5cf6',
    ADJ: '#ec4899',
    CHG: '#14b8a6'
};

const FACILITY_COLORS = ['#6366f1', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];

function FileTypeDurationChart({ runId = null, facilityFilter = '', days = 7, selectedDate = '', filteredRuns = [], selectedLicenseKey = '' }) {
    const [data, setData] = useState([]);
    const [facilities, setFacilities] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [skippedRuns, setSkippedRuns] = useState(0);
    const [viewMode, setViewMode] = useState('bar'); // 'bar' or 'pie'

    useEffect(() => {
        loadData();
    }, [runId, facilityFilter, days, selectedDate, filteredRuns, selectedLicenseKey]);

    const loadData = async () => {
        let skipped = 0;
        try {
            setLoading(true);
            setError(null);
            
            if (runId) {
                // Single run detail - show by license key
                const result = await extractLogsService.getRunDetail(runId);
                let details = result.details;
                
                // Filter by license key if selected
                if (selectedLicenseKey) {
                    details = details.filter(d => d.licenseKey === selectedLicenseKey);
                }
                
                const chartData = details.map(d => ({
                    name: d.licenseKey.slice(-6),
                    fullName: d.licenseKey,
                    ATB: d.atB_Mins || 0,
                    ZRO: d.zrO_Mins || 0,
                    BIL: d.biL_Mins || 0,
                    NOT: d.noT_Mins || 0,
                    PMT: d.pmT_Mins || 0,
                    ADJ: d.adJ_Mins || 0,
                    CHG: d.chG_Mins || 0
                }));
                setData(chartData);
                setFacilities([]);
            } else if (selectedLicenseKey) {
                // License key selected - show file type breakdown for that key
                // Use filteredRuns if date is selected, otherwise fetch all runs
                let runsToProcess;
                if (selectedDate && filteredRuns.length > 0) {
                    runsToProcess = filteredRuns.filter(r => r.status === 'Complete');
                } else {
                    const runs = await extractLogsService.getRuns(days, facilityFilter || null);
                    runsToProcess = runs.filter(r => r.status === 'Complete');
                }
                
                const fileTypeTotals = {
                    ATB: 0, ZRO: 0, BIL: 0, NOT: 0, PMT: 0, ADJ: 0, CHG: 0
                };
                
                for (const run of runsToProcess) {
                    try {
                        const detail = await extractLogsService.getRunDetail(run.runID);
                        const keyData = detail.details.find(d => d.licenseKey === selectedLicenseKey);
                        if (keyData) {
                            fileTypeTotals.ATB += keyData.atB_Mins || 0;
                            fileTypeTotals.ZRO += keyData.zrO_Mins || 0;
                            fileTypeTotals.BIL += keyData.biL_Mins || 0;
                            fileTypeTotals.NOT += keyData.noT_Mins || 0;
                            fileTypeTotals.PMT += keyData.pmT_Mins || 0;
                            fileTypeTotals.ADJ += keyData.adJ_Mins || 0;
                            fileTypeTotals.CHG += keyData.chG_Mins || 0;
                        }
                    } catch {
                        skipped++;
                    }
                }
                
                const chartData = Object.entries(fileTypeTotals)
                    .filter(([_, mins]) => mins > 0)
                    .map(([fileType, totalMins]) => ({
                        name: fileType,
                        totalMins: Math.round(totalMins * 100) / 100,
                        color: FILE_TYPE_COLORS[fileType] || '#6366f1'
                    }));
                
                setData(chartData);
                setFacilities([]);
            } else if (selectedDate && filteredRuns.length > 0) {
                // Date filter active - compute from filteredRuns
                const completedRuns = filteredRuns.filter(r => r.status === 'Complete');
                
                const fileTypeTotals = {};
                const uniqueFacilities = new Set();
                
                for (const run of completedRuns) {
                    try {
                        const detail = await extractLogsService.getRunDetail(run.runID);
                        uniqueFacilities.add(run.facilityID);
                        
                        detail.details.forEach(d => {
                            const shortFac = run.facilityID.match(/\d+$/)?.[0] || run.facilityID.slice(-6);
                            
                            ['ATB', 'ZRO', 'BIL', 'NOT', 'PMT', 'ADJ', 'CHG'].forEach(ft => {
                                const propName = ft === 'ATB' ? 'atB_Mins' :
                                                ft === 'ZRO' ? 'zrO_Mins' :
                                                ft === 'BIL' ? 'biL_Mins' :
                                                ft === 'NOT' ? 'noT_Mins' :
                                                ft === 'PMT' ? 'pmT_Mins' :
                                                ft === 'ADJ' ? 'adJ_Mins' :
                                                'chG_Mins';
                                
                                if (!fileTypeTotals[ft]) {
                                    fileTypeTotals[ft] = {};
                                }
                                if (!fileTypeTotals[ft][shortFac]) {
                                    fileTypeTotals[ft][shortFac] = 0;
                                }
                                fileTypeTotals[ft][shortFac] += d[propName] || 0;
                            });
                        });
                    } catch {
                        skipped++;
                    }
                }
                
                const facilityList = [...uniqueFacilities].map(f => f.match(/\d+$/)?.[0] || f.slice(-6));
                
                // Build chart data
                const chartData = Object.keys(fileTypeTotals).map(ft => {
                    const row = { name: ft };
                    facilityList.forEach(fac => {
                        row[fac] = Math.round((fileTypeTotals[ft][fac] || 0) * 100) / 100;
                    });
                    return row;
                }).filter(row => {
                    const total = facilityList.reduce((sum, fac) => sum + (row[fac] || 0), 0);
                    return total > 0;
                });
                
                setData(chartData);
                setFacilities(facilityList);
            } else if (facilityFilter) {
                // Single facility selected - show file type totals
                const result = await extractLogsService.getFileTypeStats(days, facilityFilter);
                const chartData = result.map(item => ({
                    name: item.fileType,
                    totalMins: Math.round(item.totalMins * 100) / 100,
                    avgMins: Math.round(item.avgMins * 100) / 100,
                    totalRows: item.totalRows,
                    extractCount: item.extractCount,
                    color: FILE_TYPE_COLORS[item.fileType] || '#6366f1'
                }));
                setData(chartData);
                setFacilities([]);
            } else {
                // All facilities - show stacked by facility
                const result = await extractLogsService.getFileTypeStatsByFacility(days);
                
                // Get unique facilities and file types
                const uniqueFacilities = [...new Set(result.map(r => r.facilityID))];
                const fileTypes = ['ATB', 'ZRO', 'BIL', 'NOT', 'PMT', 'ADJ', 'CHG'];
                
                // Pivot data: each row is a file type, columns are facilities
                const chartData = fileTypes.map(ft => {
                    const row = { name: ft };
                    uniqueFacilities.forEach(fac => {
                        const stat = result.find(r => r.fileType === ft && r.facilityID === fac);
                        const shortFac = fac.match(/\d+$/)?.[0] || fac.slice(-6);
                        row[shortFac] = stat ? Math.round(stat.totalMins * 100) / 100 : 0;
                        row[`${shortFac}_rows`] = stat ? stat.totalRows : 0;
                    });
                    return row;
                }).filter(row => {
                    // Only include file types that have data
                    const total = uniqueFacilities.reduce((sum, fac) => {
                        const shortFac = fac.match(/\d+$/)?.[0] || fac.slice(-6);
                        return sum + (row[shortFac] || 0);
                    }, 0);
                    return total > 0;
                });
                
                setData(chartData);
                setFacilities(uniqueFacilities.map(f => f.match(/\d+$/)?.[0] || f.slice(-6)));
            }
        } catch (err) {
            setError(`Couldn't load chart data: ${getErrorMessage(err)}`);
        } finally {
            setSkippedRuns(skipped);
            setLoading(false);
        }
    };

    if (loading) {
        return <div className="chart-loading">Loading chart data...</div>;
    }

    if (error) {
        return <div className="chart-empty">{error}</div>;
    }

    if (data.length === 0) {
        return <div className="chart-empty">No data available</div>;
    }

    const skippedNote = skippedRuns > 0 && (
        <p className="chart-warning">
            ⚠️ {skippedRuns} run{skippedRuns > 1 ? 's' : ''} couldn't be loaded, so this chart may be incomplete.
        </p>
    );

    // Check if we have file type breakdown (from run detail)
    const isRunDetail = data[0]?.ATB !== undefined;
    const isStackedFacilities = facilities.length > 0;
    const isSimpleFileType = data[0]?.totalMins !== undefined && !isStackedFacilities;

    if (isRunDetail) {
        // Stacked bar chart for run detail
        return (
            <div className="chart-container">
                <h3 className="chart-title">
                    {selectedLicenseKey 
                        ? `File Type Duration (Key: ...${selectedLicenseKey.slice(-8)})`
                        : `Duration by File Type (Run #${runId})`
                    }
                </h3>
                <ResponsiveContainer width="100%" height={400}>
                    <BarChart data={data} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                        <XAxis 
                            dataKey="name" 
                            stroke="#a1a1aa"
                            tick={{ fill: '#a1a1aa', fontSize: 11 }}
                            angle={-45}
                            textAnchor="end"
                            height={60}
                        />
                        <YAxis 
                            stroke="#a1a1aa"
                            tick={{ fill: '#a1a1aa', fontSize: 12 }}
                            label={{ value: 'Minutes', angle: -90, position: 'insideLeft', fill: '#a1a1aa' }}
                        />
                        <Tooltip 
                            contentStyle={{ 
                                background: 'rgba(26, 26, 46, 0.95)', 
                                border: '1px solid rgba(255,255,255,0.1)',
                                borderRadius: '8px',
                                color: '#e4e4e7'
                            }}
                            formatter={(value, name) => [`${value.toFixed(2)} min`, name]}
                            labelFormatter={(label, payload) => payload[0]?.payload?.fullName || label}
                        />
                        <Legend 
                            wrapperStyle={{ color: '#e4e4e7', paddingTop: '20px' }}
                            formatter={(value) => <span style={{ color: '#e4e4e7' }}>{value}</span>}
                        />
                        <Bar dataKey="ATB" stackId="a" fill={FILE_TYPE_COLORS.ATB} />
                        <Bar dataKey="ZRO" stackId="a" fill={FILE_TYPE_COLORS.ZRO} />
                        <Bar dataKey="BIL" stackId="a" fill={FILE_TYPE_COLORS.BIL} />
                        <Bar dataKey="NOT" stackId="a" fill={FILE_TYPE_COLORS.NOT} />
                        <Bar dataKey="PMT" stackId="a" fill={FILE_TYPE_COLORS.PMT} />
                        <Bar dataKey="ADJ" stackId="a" fill={FILE_TYPE_COLORS.ADJ} />
                        <Bar dataKey="CHG" stackId="a" fill={FILE_TYPE_COLORS.CHG} />
                    </BarChart>
                </ResponsiveContainer>
                {skippedNote}
            </div>
        );
    }

    if (isStackedFacilities) {
        // Stacked bar chart by facility
        return (
            <div className="chart-container">
                <h3 className="chart-title">
                    {selectedDate 
                        ? `Duration by File Type (${selectedDate})`
                        : 'Duration by File Type (All Facilities)'
                    }
                </h3>
                <ResponsiveContainer width="100%" height={400}>
                    <BarChart data={data} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                        <XAxis 
                            dataKey="name" 
                            stroke="#a1a1aa"
                            tick={{ fill: '#a1a1aa', fontSize: 12 }}
                        />
                        <YAxis 
                            stroke="#a1a1aa"
                            tick={{ fill: '#a1a1aa', fontSize: 12 }}
                            label={{ value: 'Minutes', angle: -90, position: 'insideLeft', fill: '#a1a1aa' }}
                        />
                        <Tooltip 
                            contentStyle={{ 
                                background: 'rgba(26, 26, 46, 0.95)', 
                                border: '1px solid rgba(255,255,255,0.1)',
                                borderRadius: '8px',
                                color: '#e4e4e7'
                            }}
                            formatter={(value, name) => [`${value} min`, name]}
                        />
                        <Legend 
                            wrapperStyle={{ color: '#e4e4e7', paddingTop: '20px' }}
                            formatter={(value) => <span style={{ color: '#e4e4e7' }}>{value}</span>}
                        />
                        {facilities.map((fac, index) => (
                            <Bar 
                                key={fac}
                                dataKey={fac} 
                                stackId="a" 
                                fill={FACILITY_COLORS[index % FACILITY_COLORS.length]} 
                                name={fac}
                            />
                        ))}
                    </BarChart>
                </ResponsiveContainer>
                {skippedNote}
            </div>
        );
    }

    // Simple file type totals view (single facility or license key selected)
    const getChartTitle = () => {
        let title = 'Duration by File Type';
        const parts = [];
        if (selectedLicenseKey) parts.push(`Key: ...${selectedLicenseKey.slice(-8)}`);
        if (selectedDate) parts.push(selectedDate);
        if (parts.length > 0) title += ` (${parts.join(' | ')})`;
        return title;
    };

    return (
        <div className="chart-container">
            <div className="chart-header">
                <h3 className="chart-title">
                    {getChartTitle()}
                </h3>
                <div className="chart-toggle">
                    <button 
                        className={`toggle-btn ${viewMode === 'bar' ? 'active' : ''}`}
                        onClick={() => setViewMode('bar')}
                    >
                        Bar
                    </button>
                    <button 
                        className={`toggle-btn ${viewMode === 'pie' ? 'active' : ''}`}
                        onClick={() => setViewMode('pie')}
                    >
                        Pie
                    </button>
                </div>
            </div>
            
            {viewMode === 'bar' ? (
                <ResponsiveContainer width="100%" height={350}>
                    <BarChart data={data} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                        <XAxis 
                            dataKey="name" 
                            stroke="#a1a1aa"
                            tick={{ fill: '#a1a1aa', fontSize: 12 }}
                        />
                        <YAxis 
                            stroke="#a1a1aa"
                            tick={{ fill: '#a1a1aa', fontSize: 12 }}
                            label={{ value: 'Total Minutes', angle: -90, position: 'insideLeft', fill: '#a1a1aa' }}
                        />
                        <Tooltip 
                            contentStyle={{ 
                                background: 'rgba(26, 26, 46, 0.95)', 
                                border: '1px solid rgba(255,255,255,0.1)',
                                borderRadius: '8px',
                                color: '#e4e4e7'
                            }}
                            content={({ active, payload }) => {
                                if (active && payload && payload.length) {
                                    const item = payload[0].payload;
                                    return (
                                        <div style={{ 
                                            background: 'rgba(26, 26, 46, 0.95)', 
                                            border: '1px solid rgba(255,255,255,0.1)',
                                            borderRadius: '8px',
                                            padding: '12px',
                                            color: '#e4e4e7'
                                        }}>
                                            <div style={{ fontWeight: 600, marginBottom: '8px' }}>{item.name}</div>
                                            <div style={{ fontSize: '13px' }}>Total: <strong>{item.totalMins} min</strong></div>
                                            {item.avgMins && <div style={{ fontSize: '13px' }}>Avg: <strong>{item.avgMins} min</strong></div>}
                                            {item.totalRows && <div style={{ fontSize: '13px' }}>Rows: <strong>{item.totalRows.toLocaleString()}</strong></div>}
                                            {item.extractCount && <div style={{ fontSize: '13px' }}>Extracts: <strong>{item.extractCount}</strong></div>}
                                        </div>
                                    );
                                }
                                return null;
                            }}
                        />
                        <Bar dataKey="totalMins" radius={[4, 4, 0, 0]}>
                            {data.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={entry.color || FILE_TYPE_COLORS[entry.name] || '#6366f1'} />
                            ))}
                        </Bar>
                    </BarChart>
                </ResponsiveContainer>
            ) : (
                <ResponsiveContainer width="100%" height={350}>
                    <PieChart>
                        <Pie
                            data={data}
                            cx="50%"
                            cy="50%"
                            outerRadius={120}
                            dataKey="totalMins"
                            nameKey="name"
                            label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                        >
                            {data.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={entry.color || FILE_TYPE_COLORS[entry.name] || '#6366f1'} />
                            ))}
                        </Pie>
                        <Tooltip 
                            contentStyle={{ 
                                background: 'rgba(26, 26, 46, 0.95)', 
                                border: '1px solid rgba(255,255,255,0.1)',
                                borderRadius: '8px',
                                color: '#e4e4e7'
                            }}
                            formatter={(value) => [`${value} min`, 'Duration']}
                        />
                    </PieChart>
                </ResponsiveContainer>
            )}
            {skippedNote}
        </div>
    );
}

export default FileTypeDurationChart;
